<template>
  <div
    class="page-content"
    :class="pageId"
  >
    <general-info
      :is-editable="isEditable"
      :caller-info="customerInfo"
      @save="updateProfileInfo"
      @cancel="onCancel"
    />

    <contact-info
      :is-editable="isEditable"
      :caller-info="customerInfo"
      @save="updateProfileInfo"
      @cancel="onCancel"
    />

    <home-address
      :is-editable="isEditable"
      :caller-info="customerInfo"
      @save="updateProfileInfo"
      @cancel="onCancel"
    />

    <care-notes
      :is-editable="isEditable"
      :caller-info="customerInfo"
      @save="updateProfileInfo"
      @cancel="onCancel"
    />

    <ride-preference
      :is-editable="isEditable"
      :caller-info="customerInfo"
      @save="updateProfileInfo"
      @cancel="onCancel"
    />

    <auto-tip-info
      :is-editable="isEditable"
      :caller-info="customerInfo"
      @save="updateProfileInfo"
      @cancel="onCancel"
    />

    <section v-if="isPartnerInfoShow">
      <partner-info :caller-info="customerInfo" />
    </section>

    <section v-if="isFamilyUser || isCustomerUser">
      <change-password :caller-id="customerInfo.id" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, toRefs, watch, computed, nextTick, onMounted, defineAsyncComponent } from 'vue';
import { cloneDeep } from 'lodash';
import MomentTimezone from 'moment-timezone';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useProfile } from '@/use/profile';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useRide } from '@/use/ride';
import { CustomLocationType } from '@/models/address';
import { CallerAddress, CallerInput } from '@/models/caller';

import { callerPlanService } from '@/services/callerPlan';
import { callerService } from '@/services/caller';

const GeneralInfo = defineAsyncComponent(() => import('./Profile/GeneralInfo.vue') as any);

const ContactInfo = defineAsyncComponent(() => import('./Profile/ContactInfo.vue') as any);

const HomeAddress = defineAsyncComponent(() => import('./Profile/HomeAddress.vue') as any);

const CareNotes = defineAsyncComponent(() => import('./Profile/CareNotes.vue') as any);

const RidePreference = defineAsyncComponent(() => import('./Profile/RidePreference.vue') as any);

const AutoTipInfo = defineAsyncComponent(() => import('./Profile/AutoTipInfo.vue') as any);

const PartnerInfo = defineAsyncComponent(() => import('./Profile/PartnerInfo.vue') as any);

const ChangePassword = defineAsyncComponent(() => import('@/components/UIComponents/Account/ChangePassword.vue') as any);

const pageId = 'family-profile';

const { currentUser, userRoles } = useProfile();
const { isCustomerUser, isFamilyUser } = toRefs(userRoles);
const { customerPhoneNumber, setCustomerInfo, setCustomerActivePlan, setCustomerPhoneNumber, setCompletedLastRideAddress } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();
const { getCompletedLastRideAddress } = useRide();

const customerInfo: Ref<any> = ref({});
const cachedCustomerInfo: Ref<any> = ref({});
const customAddresses: Ref<CustomLocationType[]> = ref([]);

const isEditable = computed(() => {
  return Boolean(customerPhoneNumber.value && customerInfo.value.id) || isFamilyUser.value || isCustomerUser.value;
});

const isPartnerInfoShow = computed(() => !!customerInfo.value.partner?.id);

const loadUser = async () => {
  setCustomerActivePlan(null);

  const loading = ElLoading.service({
    lock: true,
    text: 'Loading Profile...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    const caller = await callerService.fetchCustomerInfo({
      phoneNumber: customerPhoneNumber.value,
    });

    customerInfo.value = cloneDeep(caller);
    if (customerInfo.value.dob) {
      customerInfo.value.dob = MomentTimezone(customerInfo.value.dob).toDate();
    }

    await getCustomAddresses(customerInfo.value.id);

    const address = await getCompletedLastRideAddress(customerInfo.value.id);
    setCompletedLastRideAddress(address);

    cachedCustomerInfo.value = cloneDeep(customerInfo.value);

    setCustomerInfo(customerInfo.value);

    setActiveCallerSubscription();
  } catch (error) {
    customerInfo.value = {};
    cachedCustomerInfo.value = {};
    setCustomerInfo({});

    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  loading.close();
};

const setActiveCallerSubscription = async () => {
  try {
    const planInfo = await callerPlanService.fetchActiveCallerSubscription({ phoneNumber: customerPhoneNumber.value });

    if (!planInfo) {
      return;
    }

    setCustomerActivePlan(planInfo);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

const updateProfileInfo = async (data: CallerInput) => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Updating Profile...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    const caller = await callerService.updateCaller({
      callerId: customerInfo.value.id,
      data,
    });

    customerInfo.value = cloneDeep(caller);
    customerInfo.value.customAddresses = customAddresses.value;

    if (customerInfo.value.dob) {
      customerInfo.value.dob = MomentTimezone(customerInfo.value.dob).toDate();
    }

    cachedCustomerInfo.value = cloneDeep(customerInfo.value);

    setCustomerInfo(customerInfo.value);
    showSuccessPopupDialog({
      text: 'The profile has been updated successfully.',
    });
  } catch (error) {
    customerInfo.value = cloneDeep(cachedCustomerInfo.value);

    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  loading.close();
};

const getCustomAddresses = async (callerId: string) => {
  try {
    const response = await callerService.fetchCustomerAddress({
      callerId,
    });

    customAddresses.value = response.rows.map(
      (customLocation: CallerAddress, index: number) =>
        ({
          ...customLocation,
          id: customLocation.id.toString(),
          index,
        }) as CustomLocationType,
    );

    customerInfo.value.customAddresses = customAddresses.value;
    cachedCustomerInfo.value = cloneDeep(customerInfo.value);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

const onCancel = () => {
  customerInfo.value = cloneDeep(cachedCustomerInfo.value);
};

watch(customerPhoneNumber, (value: string) => {
  if (value) {
    loadUser();
  }
});

onMounted(async () => {
  await nextTick();
  util.moveToTop();

  if (!isFamilyUser.value && !isCustomerUser.value) {
    setCustomerInfo({});
  }

  if (customerPhoneNumber.value) {
    loadUser();
  } else if (isCustomerUser.value || isFamilyUser.value) {
    setCustomerPhoneNumber(currentUser.value!.phoneNumber ?? '');
  }
});
</script>

<style lang="scss">
.custom-tooltip.note-info {
  width: 400px !important;
  line-height: 20px;

  span {
    color: #e9ec0f;
    font-weight: bold;
  }
}

.family-profile {
  &.page-content {
    form.view {
      input,
      textarea,
      .content-view {
        color: #925a18;
        height: unset;
      }
      .content-view {
        white-space: break-spaces;
        &.clickable {
          pointer-events: auto;
          cursor: pointer;
        }
      }
      .input-group .input-group-addon {
        width: 13px;
      }
    }
  }
  .editable-card {
    .card {
      max-width: 960px;
    }
  }
}
</style>
